import { X, BookOpen, HelpCircle } from 'lucide-react';
import { useState } from 'react';

interface GlossaryTerm {
  term: string;
  definition: string;
  example?: string;
}

const glossaryTerms: GlossaryTerm[] = [
  {
    term: "Base Imponible",
    definition: "Es la cantidad sobre la que se calculan los impuestos. En el caso de tus facturas, es el importe sin IVA.",
    example: "Si facturas 1.000€ + 21% IVA (210€), tu base imponible es 1.000€"
  },
  {
    term: "IRPF (Impuesto sobre la Renta)",
    definition: "Impuesto que se paga sobre tus beneficios. Se calcula por tramos: cuanto más ganas, mayor porcentaje pagas en la parte que supera cada tramo.",
    example: "Si ganas 30.000€ netos, no pagas el mismo % sobre todo, sino que cada tramo tiene su tipo"
  },
  {
    term: "Retención",
    definition: "Anticipo de IRPF que te descuentan en cada factura y que pagas a Hacienda trimestralmente. Luego se ajusta en la declaración anual.",
    example: "Factura de 1.000€: si tienes retención del 15%, te pagan 850€ y los otros 150€ los ingresas tú a Hacienda"
  },
  {
    term: "Cuota de autónomos (RETA)",
    definition: "Es la cuota mensual que pagas a la Seguridad Social. Desde 2023 depende de tus ingresos reales. Es un gasto deducible.",
    example: "Puedes pagar desde 230€/mes hasta 530€/mes según tus rendimientos"
  },
  {
    term: "Tarifa Plana",
    definition: "Bonificación para nuevos autónomos: cuota base de 80€/mes + MEI durante el primer año (en lugar de ~300€). Requisito: no haber estado de alta en los últimos 2-3 años.",
    example: "Durante 12 meses pagas 80€/mes + MEI"
  },
  {
    term: "IVA Soportado",
    definition: "IVA que TÚ pagas en tus gastos. Puedes deducírtelo (recuperarlo) del IVA que cobras a tus clientes.",
    example: "Compras un ordenador por 1.000€ + 210€ IVA → Los 210€ los recuperas"
  },
  {
    term: "IVA Repercutido",
    definition: "IVA que TÚ cobras a tus clientes y debes ingresar a Hacienda trimestralmente.",
    example: "Facturas 1.000€ + 210€ IVA → Los 210€ se los das a Hacienda (menos el IVA soportado)"
  },
  {
    term: "Modelo 130",
    definition: "Declaración trimestral donde pagas un anticipo del 20% de tus beneficios a cuenta del IRPF anual. Solo si NO te hacen retención.",
    example: "Si en Q1 ganaste 5.000€ netos → pagas ~1.000€ en el modelo 130"
  },
  {
    term: "Modelo 303",
    definition: "Declaración trimestral de IVA. Declaras el IVA cobrado (repercutido) menos el IVA pagado (soportado).",
    example: "Cobrado 2.100€ IVA, pagado 420€ IVA → Ingresas 1.680€ a Hacienda"
  },
  {
    term: "Rendimiento Neto",
    definition: "Tus ingresos menos todos los gastos deducibles y la cuota de autónomos. Es la base sobre la que se calcula el IRPF.",
    example: "Ingresos 50.000€ - Gastos 10.000€ - cuota de autónomos 3.600€ = 36.400€ rendimiento neto"
  },
  {
    term: "Tipo Efectivo",
    definition: "El porcentaje real que pagas de IRPF sobre tu base. Es diferente al tipo marginal (el del último tramo).",
    example: "IRPF total 6.000€ / Base 30.000€ = 20% tipo efectivo"
  },
  {
    term: "Gastos Deducibles",
    definition: "Gastos relacionados con tu actividad profesional que puedes restar de tus ingresos para pagar menos impuestos.",
    example: "Oficina, ordenador, software, formación, viajes de trabajo, etc."
  },
  {
    term: "Mínimo Personal y Familiar",
    definition: "Cantidad sobre la que NO pagas IRPF. Aumenta si tienes hijos, ascendientes a cargo o discapacidad. Reduce tu cuota final.",
    example: "Mínimo personal 5.550€ + 2.400€ por primer hijo = 7.950€ exentos de IRPF"
  },
  {
    term: "Inversión del Sujeto Pasivo",
    definition: "Régimen especial de IVA: el cliente (no tú) es quien debe ingresar el IVA. Se usa en servicios a empresas UE y algunos sectores.",
    example: "Consultoría a empresa francesa: facturas 1.000€ sin IVA, ellos lo liquidan"
  },
  {
    term: "Gastos de Difícil Justificación",
    definition: "Reducción automática del 5% sobre tu rendimiento neto (máximo 2.000€) para compensar pequeños gastos sin factura.",
    example: "Rendimiento 40.000€ → Reducción de 2.000€ (5%) aplicada automáticamente"
  },
  {
    term: "Vivienda Afecta",
    definition: "Porcentaje de tu casa que usas exclusivamente para trabajar. Solo esa parte de los suministros (30%) es deducible.",
    example: "Casa 100m², oficina 15m² = 15% afecta → Deduces 30% de (15% de suministros)"
  }
];

export default function HelpGlossary({ onClose }: { onClose?: () => void }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState<'glossary' | 'guide'>('glossary');

  const filteredTerms = glossaryTerms.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BookOpen size={24} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e293b' }}>
              Centro de Ayuda
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <X size={24} color="#64748b" />
          </button>
        </div>

        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <button
              onClick={() => setActiveSection('glossary')}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                background: activeSection === 'glossary' ? '#2563eb' : '#f1f5f9',
                color: activeSection === 'glossary' ? 'white' : '#64748b',
                transition: 'all 0.2s'
              }}
            >
              Glosario de términos
            </button>
            <button
              onClick={() => setActiveSection('guide')}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                background: activeSection === 'guide' ? '#2563eb' : '#f1f5f9',
                color: activeSection === 'guide' ? 'white' : '#64748b',
                transition: 'all 0.2s'
              }}
            >
              Guía rápida
            </button>
          </div>

          {activeSection === 'glossary' && (
            <input
              type="text"
              placeholder="Buscar término..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          )}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {activeSection === 'glossary' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {filteredTerms.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '20px',
                    background: '#f9fafb',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 8px',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#1e293b'
                    }}
                  >
                    {item.term}
                  </h3>
                  <p
                    style={{
                      margin: '0 0 12px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#475569'
                    }}
                  >
                    {item.definition}
                  </p>
                  {item.example && (
                    <div
                      style={{
                        padding: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        borderLeft: '3px solid #2563eb'
                      }}
                    >
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#2563eb',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Ejemplo
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                        {item.example}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <QuickGuide />
          )}
        </div>
      </div>
    </div>
  );
}

function QuickGuide() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
          Cómo usar esta calculadora
        </h3>
        <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#64748b', margin: 0 }}>
          Sigue estos pasos para obtener una simulación precisa de tu situación fiscal como autónomo.
        </p>
      </div>

      <GuideStep
        number={1}
        title="Configura tu perfil"
        description="Selecciona tu región (afecta a los tramos de IRPF autonómico) y el año de la simulación."
      />

      <GuideStep
        number={2}
        title="Añade tus ingresos"
        description="Introduce todas tus fuentes de ingresos. Para cada una, indica la base imponible (sin IVA), el tipo de IVA y el porcentaje de retención IRPF que te aplican tus clientes."
      />

      <GuideStep
        number={3}
        title="Registra tus gastos"
        description="Añade todos los gastos relacionados con tu actividad: oficina, software, formación, viajes, etc. Indica el porcentaje deducible según el uso profesional (100% profesional, 50% mixto para vehículo/teléfono, 30% para suministros)."
      />

      <GuideStep
        number={4}
        title="Configura la cuota de autónomos"
        description="Si eres nuevo autónomo, marca la Tarifa Plana (80€/mes + MEI). Si no, puedes dejar que se calcule automáticamente según tus ingresos o introducir tu cuota manualmente."
      />

      <GuideStep
        number={5}
        title="Completa tu situación personal"
        description="Añade información sobre seguros médicos, hijos, familia numerosa, ascendientes a cargo, planes de pensiones y donativos. Todo esto reduce tu IRPF final."
      />

      <GuideStep
        number={6}
        title="Revisa tus resultados"
        description="Verás cuánto pagas en IRPF, cuota de autónomos, IVA, y lo más importante: cuánto te queda realmente en el bolsillo después de impuestos."
      />

      <div
        style={{
          padding: '20px',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '12px',
          border: '1px solid #93c5fd',
          marginTop: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <HelpCircle size={20} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: '#1e40af' }}>
              Consejos para mejores resultados
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
              <li>Usa datos reales o estimaciones conservadoras</li>
              <li>Incluye TODOS los gastos deducibles (muchos autónomos olvidan algunos)</li>
              <li>Si tienes dudas sobre un porcentaje de deducción, consulta con un gestor</li>
              <li>Guarda tus simulaciones para compararlas más adelante</li>
              <li>La simulación es orientativa; cada caso puede tener particularidades</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function GuideStep({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 700,
          flexShrink: 0
        }}
      >
        {number}
      </div>
      <div>
        <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
          {title}
        </h4>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#64748b' }}>
          {description}
        </p>
      </div>
    </div>
  );
}
